import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import { post, put } from '../../services/api';
import { usePageLoader } from '../../contexts/PageLoaderContext';
import { useNotifier } from '../../contexts/NotificationContext';
import {FormBuilderProvider, useFormBuilder} from '../../contexts/FormBuilderContext'; // <--- Импортируем наш Provider

import Card from '../common/Card/Card';
import Input from '../common/Input/Input';
import Textarea from '../common/Textarea/Textarea';
import Switch from '../common/Switch/Switch';
import AddQuestionMenu from './AddQuestionMenu/AddQuestionMenu';
import SortableQuestionItem from './SortableQuestionItem/SortableQuestionItem';
import FullScreenMessage from '../common/FullScreenMessage/FullScreenMessage';
import { QUESTION_CONFIG, QUESTION_TYPES } from './questionTypes';
import styles from './FormBuilder.module.css';
import { FiPlus, FiSettings, FiList, FiFileText } from 'react-icons/fi';

import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, TouchSensor, DragOverlay  } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import Button from "../common/Button/Button";
import QuestionEditor from "./QuestionEditor/QuestionEditor"; // <-- Добавьте этот импорт, он нужен для оверлея

const MAX_QUESTIONS = 20;

const FormBuilder = ({ initialForm = null }) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { showPageLoader, hidePageLoader } = usePageLoader();
    const { addNotification } = useNotifier();

    // const isEditMode = !!initialForm;
    const isEditMode = initialForm?.id;

    const [activeDragItem, setActiveDragItem] = useState(null);

    const [formTitle, setFormTitle] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [questions, setQuestions] = useState([]);
    const [submitButtonText, setSubmitButtonText] = useState('');
    const [submissionMessageTitle, setSubmissionMessageTitle] = useState('');
    const [submissionMessageText, setSubmissionMessageText] = useState('');
    const [allowMultipleSubmissions, setAllowMultipleSubmissions] = useState(true);
    const [activeTab, setActiveTab] = useState('questions');
    const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
    const [receiveNotificationEmail, setReceiveNotificationEmail] = useState(false);
    const [receiveNotificationTelegram, setReceiveNotificationTelegram] = useState(false);

    const canAddQuestion = questions.length < MAX_QUESTIONS;

    useEffect(() => {
        // if (isEditMode && initialForm) {
        if (initialForm) {
            setFormTitle(initialForm.title || '');
            setFormDescription(initialForm.description || '');
            setQuestions(initialForm.questions || []);
            const settings = initialForm.settings || {};
            setSubmitButtonText(settings.submit_button_text || intl.formatMessage({ id: "formSettings.submitButtonText.placeholder" }));
            setSubmissionMessageTitle(settings.submission_message.title || intl.formatMessage({ id: "formSettings.submissionMessage.title.placeholder" }));
            setSubmissionMessageText(settings.submission_message.text || intl.formatMessage({ id: "formSettings.submissionMessage.text.placeholder" }));
            // setReceiveNotifications(settings.receive_notifications ?? true);
            setReceiveNotificationEmail(settings?.notifications?.email?.enabled ?? false);
            setReceiveNotificationTelegram(settings?.notifications?.telegram?.enabled ?? false);
            setAllowMultipleSubmissions(settings.allow_multiple_submissions ?? true);
        } else {
            setFormTitle(intl.formatMessage({ id: "create.formTitlePlaceholder" }));
            setSubmitButtonText(intl.formatMessage({ id: "formSettings.submitButtonText.placeholder" }));
            setSubmissionMessageTitle(intl.formatMessage({ id: "formSettings.submissionMessage.title.placeholder" }));
            setSubmissionMessageText(intl.formatMessage({ id: "formSettings.submissionMessage.text.placeholder" }));
        }
    }, [initialForm, isEditMode, intl]);

    // --- ФУНКЦИИ-ОБРАБОТЧИКИ ---
    // Они больше не обернуты в useCallback, что решает проблему со "старыми" состояниями.

    const createNewOption = (optionsCount) => {
        const newOptionText = intl.formatMessage(
            { id: "question.defaultOptionText" }, // ID нашего сообщения
            { number: optionsCount + 1 }          // Значения для подстановки
        );
        return {
            text: newOptionText,
            temp_id: `temp_${crypto.randomUUID()}`
        };
    };

    const addQuestion = (type) => {
        const config = QUESTION_CONFIG.find(c => c.type === type);
        if (!config) return;
        const newQuestion = {
            temp_id: `temp_${crypto.randomUUID()}`,
            type: config.type,
            text: config.defaultTextId ? intl.formatMessage({ id: config.defaultTextId }) : '',
            // Используем нашу новую функцию для создания опций по умолчанию
            options: (config.defaultOptions || []).map(() => createNewOption(0)),
            is_required: config.default_is_required ?? false,
            config: config.config || {},
        };
        setQuestions(prev => [...prev, newQuestion]);
        setIsAddMenuOpen(false);
    };

    const updateQuestion = (questionId, updatedData) => {
        setQuestions(prev => prev.map(q => (q.id === questionId || q.temp_id === questionId) ? { ...q, ...updatedData } : q));
    };

    const deleteQuestion = (questionId) => {
        setQuestions(prev => prev.filter(q => (q.id || q.temp_id) !== questionId));
    };

    const duplicateQuestion = (questionId) => {
        setQuestions(prev => {
            const index = prev.findIndex(q => (q.id || q.temp_id) === questionId);
            if (index === -1) return prev;
            const original = prev[index];
            const newQuestions = [...prev];
            const duplicatedItem = JSON.parse(JSON.stringify(original));
            delete duplicatedItem.id; duplicatedItem.temp_id = `temp_${crypto.randomUUID()}`;
            if (duplicatedItem.options) { duplicatedItem.options.forEach(opt => { delete opt.id; opt.temp_id = `temp_${crypto.randomUUID()}`; }); }
            newQuestions.splice(index + 1, 0, duplicatedItem);
            return newQuestions;
        });
    };

    const addOption = (questionId) => {
        setQuestions(prev => prev.map(q => {
            if ((q.id || q.temp_id) === questionId) {
                const currentOptions = q.options || [];
                // Используем нашу новую функцию
                const newOption = createNewOption(currentOptions.length);
                return { ...q, options: [...currentOptions, newOption] };
            }
            return q;
        }));
    };

    const updateOption = (questionId, optionId, newText) => {
        setQuestions(prev => prev.map(q => {
            if ((q.id || q.temp_id) === questionId) {
                const newOptions = q.options.map(opt => ((opt.id || opt.temp_id) === optionId) ? { ...opt, text: newText, label: newText } : opt);
                return { ...q, options: newOptions };
            } return q;
        }));
    };

    const deleteOption = (questionId, optionId) => {
        setQuestions(prev => prev.map(q => {
            if ((q.id || q.temp_id) === questionId) {
                return { ...q, options: q.options.filter(opt => (opt.id || opt.temp_id) !== optionId) };
            } return q;
        }));
    };

    // const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
    const sensors = useSensors(
        // Оставляем PointerSensor для мыши
        useSensor(PointerSensor, {
            // Требуем небольшое движение мыши перед началом перетаскивания, чтобы не было ложных срабатываний
            activationConstraint: {
                distance: 8,
            },
        }),
        // Добавляем TouchSensor для мобильных устройств
        useSensor(TouchSensor, {
            // Пользователь должен задержать палец на 250мс, чтобы началось перетаскивание, а не скролл
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
        // Оставляем KeyboardSensor для доступности
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = (event) => {
        const { active } = event;
        // Находим и сохраняем в состояние не технический объект, а наш полный объект вопроса
        setActiveDragItem(questions.find(q => (q.id || q.temp_id) === active.id));
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active && over && active.id !== over.id) {
            setQuestions((items) => {
                const oldIndex = items.findIndex(item => (item.id || item.temp_id) === active.id);
                const newIndex = items.findIndex(item => (item.id || item.temp_id) === over.id);
                return arrayMove(items, oldIndex, newIndex);
            });
        }
        setActiveDragItem(null);
    };

    const handleSave = async () => {
        showPageLoader(intl.formatMessage({ id: "formBuilder.saving", defaultMessage: "Saving..." }));

        let settings = {
            submit_button_text: submitButtonText,
            submission_message: {
                title: submissionMessageTitle,
                text: submissionMessageText
            },
            allow_multiple_submissions: allowMultipleSubmissions,
            notifications: {
                email: { enabled: receiveNotificationEmail },
                telegram: { enabled: receiveNotificationTelegram }
            }
        };

        const formData = {
            title: formTitle,
            description: formDescription,
            settings: settings,
            questions: questions.map((question, qIndex) => {
                const { temp_id, ...restOfQuestion } = question;
                return {
                    ...restOfQuestion, order: qIndex,
                    options: (question.options || []).map((option, oIndex) => {
                        const { temp_id: opt_temp_id, ...restOfOption } = option;
                        return { ...restOfOption, order: oIndex };
                    }),
                };
            }),
        };
        try {
            const response = isEditMode ? await put(`forms/${initialForm.id}`, formData) : await post('forms', formData);
            if (response.data?.id) {
                addNotification(intl.formatMessage({ id: isEditMode ? "formBuilder.update.success" : "formBuilder.save.success" }), 'success');
                navigate(`/form/${response.data.id}/settings`);
            } else { throw new Error('Invalid response from server'); }
        } catch (error) {
            addNotification(error.message || intl.formatMessage({ id: isEditMode ? "formBuilder.update.error" : "formBuilder.save.error" }), 'error');
        } finally {
            hidePageLoader();
        }
    };

    // --- СОЗДАЕМ ОБЪЕКТ СО ВСЕМИ ДАННЫМИ И ФУНКЦИЯМИ ДЛЯ КОНТЕКСТА ---
    const contextValue = useMemo(() => ({
        questions, updateQuestion, deleteQuestion, duplicateQuestion,
        addOption, updateOption, deleteOption,
        sensors, handleDragEnd
    }), [questions]); // Зависимость от questions, чтобы функции в контексте всегда имели доступ к актуальному состоянию

    let isTelegramEnv = true;

    const draggedQuestion = activeDragItem ? questions.find(q => (q.id || q.temp_id) === activeDragItem.id) : null;

    return (
        <FormBuilderProvider value={contextValue}>
            <div className={styles.formBuilder}>

                <div className={styles.tabNavigation}>
                    <button className={`${styles.tabButton} ${activeTab === 'questions' ? styles.activeTab : ''}`} onClick={() => setActiveTab('questions')}>
                        <FiList /> <FormattedMessage id="formBuilder.tab.questions" />
                    </button>
                    <button className={`${styles.tabButton} ${activeTab === 'formSettings' ? styles.activeTab : ''}`} onClick={() => setActiveTab('formSettings')}>
                        <FiSettings /> <FormattedMessage id="formBuilder.tab.settings" />
                    </button>
                </div>

                <div className={styles.tabContent}>
                    {activeTab === 'questions' ? (
                        <>
                            <Card>
                                <Input type="text" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} placeholder={intl.formatMessage({ id: "create.formTitlePlaceholder" })} />
                                <Textarea value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder={intl.formatMessage({ id: "create.formDescriptionPlaceholder" })} rows="2" />
                            </Card>

                            {questions.length === 0 && (
                                <p className={styles.noQuestionsPlaceholder}>
                                    <FormattedMessage id="create.noQuestionsPlaceholder"/>
                                </p>
                            )}
                            {/*{questions.length === 0 && (*/}
                            {/*    <FullScreenMessage*/}
                            {/*        icon={<FiFileText size={48} />}*/}
                            {/*        title={intl.formatMessage({ id: "create.noQuestionsPlaceholder.title" })}*/}
                            {/*        text={intl.formatMessage({ id: "create.noQuestionsPlaceholder.text" })}*/}
                            {/*    />*/}
                            {/*)}*/}

                            {/* --- ИЗМЕНЕНИЕ: Добавляем обработчики и DragOverlay в DndContext --- */}
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                                onDragCancel={() => setActiveDragItem(null)}
                                modifiers={[restrictToVerticalAxis]}
                            >
                                <SortableContext items={questions.map(q => q.id || q.temp_id)} strategy={verticalListSortingStrategy}>
                                    {questions.map((q, index) => (
                                        <SortableQuestionItem
                                            key={q.id || q.temp_id}
                                            question={q}
                                            questionNumber={index + 1}
                                            // Передаем все колбэки, как и раньше
                                            onUpdateQuestion={updateQuestion}
                                            onDeleteQuestion={deleteQuestion}
                                            onDuplicateQuestion={duplicateQuestion}
                                            onAddOption={addOption}
                                            onUpdateOption={updateOption}
                                            onDeleteOption={deleteOption}
                                        />
                                    ))}
                                </SortableContext>

                                <DragOverlay>
                                    {/* Теперь мы просто проверяем, есть ли объект в activeDragItem */}
                                    {activeDragItem ? (
                                        <div className={styles.dragOverlayItem}>
                                            <QuestionEditor
                                                question={activeDragItem}
                                                questionNumber={questions.findIndex(q => (q.id || q.temp_id) === (activeDragItem.id || activeDragItem.temp_id)) + 1}
                                            />
                                        </div>
                                    ) : null}
                                </DragOverlay>
                            </DndContext>
                        </>
                    ) : (
                        <Card className={styles.formSettingsPanel}>
                            <Input id="submitButtonText" label={<FormattedMessage id="formSettings.submitButtonText.label" />} value={submitButtonText} onChange={(e) => setSubmitButtonText(e.target.value)} placeholder={intl.formatMessage({ id: "formSettings.submitButtonText.placeholder" })} />

                            {/*<Textarea id="submissionMessage" label={<FormattedMessage id="formSettings.submissionMessage.label" />} value={submissionMessage} onChange={(e) => setSubmissionMessage(e.target.value)} placeholder={intl.formatMessage({ id: "formSettings.submissionMessage.placeholder" })} rows="3" />*/}

                            <Input
                                label={<FormattedMessage id="formSettings.submissionMessage.titleLabel" />}
                                value={submissionMessageTitle || ''}
                                onChange={(e) => setSubmissionMessageTitle(e.target.value)}
                            />

                            <Textarea
                                label={<FormattedMessage id="formSettings.submissionMessage.textLabel" />}
                                value={submissionMessageText || ''}
                                onChange={(e) => setSubmissionMessageText(e.target.value)}
                                rows="3"
                            />

                            {/*<div className={styles.switchSettingContainer}><Switch id="receiveNotifications" checked={receiveNotifications} onChange={() => setReceiveNotifications(prev => !prev)} labelLeft={intl.formatMessage({ id: "formSettings.receiveNotifications.label" })} /></div>*/}

                            <div className={styles.switchSettingContainer}><Switch id="allowMultipleSubmissions" checked={allowMultipleSubmissions} onChange={() => setAllowMultipleSubmissions(prev => !prev)} labelLeft={intl.formatMessage({ id: "formSettings.allowMultipleSubmissions.label" })} /></div>

                            <div className={styles.switchSettingContainer}>
                                <Switch
                                    id="receiveTgNotifications"
                                    checked={receiveNotificationTelegram}
                                    onChange={() => setReceiveNotificationTelegram(prev => !prev)}
                                    labelLeft={intl.formatMessage({ id: "formSettings.notifications.telegram.label" })}
                                />
                            </div>

                            {/* Показываем этот блок, только если мы НЕ в Telegram */}
                            {!isTelegramEnv && (
                                <div className={styles.switchSettingContainer}>
                                    <Switch
                                        id="receiveEmailNotifications"
                                        checked={receiveNotificationEmail}
                                        onChange={() => setReceiveNotificationEmail(prev => !prev)}
                                        labelLeft={intl.formatMessage({ id: "formSettings.notifications.email.label" })}
                                    />
                                </div>
                            )}

                        </Card>
                    )}
                </div>

                {activeTab === 'questions' && (
                    <button
                        className={`${styles.fab} ${!canAddQuestion ? styles.disabled : ''}`}
                        onClick={() => {
                            if (canAddQuestion) {
                                setIsAddMenuOpen(true);
                            } else {
                                addNotification('Достигнут лимит на количество вопросов', 'error');
                            }
                        }}
                        // disabled={!canAddQuestion} // Можно использовать disabled, но кастомный стиль дает больше гибкости
                        title={intl.formatMessage({ id: "addQuestionMenu.title" })}
                    >
                        <FiPlus />
                    </button>
                )}
                <AddQuestionMenu isOpen={isAddMenuOpen} onClose={() => setIsAddMenuOpen(false)} onSelectQuestionType={addQuestion} />

                <div className={styles.footerActions}>
                    <Button size="full" onClick={handleSave} disabled={!questions.length}>
                        {isEditMode
                            ? <FormattedMessage id="formBuilder.updateButton" />
                            : <FormattedMessage id="formBuilder.saveButton" />}
                    </Button>
                </div>
            </div>
        </FormBuilderProvider>
    );
};

// --- ВНУТРЕННИЙ КОМПОНЕНТ ДЛЯ ОТОБРАЖЕНИЯ ПАНЕЛИ ВОПРОСОВ ---
// Он берет все необходимое из контекста
const QuestionsPanel = () => {
    const { questions, sensors, handleDragEnd } = useFormBuilder();
    const intl = useIntl();
    return (
        <>
            {questions.length === 0 && (
                <p className={styles.noQuestionsPlaceholder}>
                    <FormattedMessage id="create.noQuestionsPlaceholder"/>
                </p>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd} modifiers={[restrictToVerticalAxis]}>
                <SortableContext items={questions.map(q => q.id || q.temp_id)} strategy={verticalListSortingStrategy}>
                    {questions.map((q, index) => (
                        <SortableQuestionItem
                            key={q.id || q.temp_id}
                            question={q}
                            questionNumber={index + 1}
                        />
                    ))}
                </SortableContext>
            </DndContext>
        </>
    );
};

export default FormBuilder;
